import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { type SelectScreenType } from '@models/ui/lists'
import { type PointPaymentMethodType, type PointType } from '@models/entities/points'
import { type CountryType } from '@models/entities/country'
import {
  type PayoutMethodType,
  type CryptoWalletBlockchainType
} from '@models/entities/payout'
import { type TransactionType } from '@models/entities/transactions'
import { type SettlementType } from '@models/entities/settlements'
import { type UserType } from '@models/entities/users'

export const useSelectStore = defineStore('useSelectStore', () => {
  const isShownLanguages = ref(false)
  const isShownSelectLanguageScreen = ref(false)
  const selectedPoint = ref<PointType | null>(null)
  const selectedTransaction = ref<TransactionType | null>(null)
  const selectedSettlement = ref<SettlementType | null>(null)
  const selectedUser = ref<UserType | null>(null)
  const paymentMethodField = ref<SelectScreenType<PointPaymentMethodType>>({
    isTouchedField: false,
    isShown: false,
    currentVal: null
  })
  const countryField = ref<SelectScreenType<CountryType>>({
    isTouchedField: false,
    isShown: false,
    currentVal: null
  })
  const bankField = ref<SelectScreenType<string>>({
    isTouchedField: false,
    isShown: false,
    currentVal: ''
  })
  const selectedPayoutMethod = ref<PayoutMethodType | null>(null)
  const selectedBlockchain = ref<CryptoWalletBlockchainType | null>(null)

  const isShownSelectPaymentMethodScreen = computed(() => paymentMethodField.value.isShown)
  const selectedPaymentMethod = computed<PointPaymentMethodType | null>(() => paymentMethodField.value.currentVal)
  const isTouchedPaymentMethodSelectField = computed(() => paymentMethodField.value.isTouchedField)
  const isShownSelectCountryScreen = computed(() => countryField.value.isShown)
  const selectedCountry = computed<CountryType | null>(() => countryField.value.currentVal)
  const isTouchedCountrySelectField = computed(() => countryField.value.isTouchedField)
  const isShownSelectBankScreen = computed(() => bankField.value.isShown)
  const selectedBank = computed(() => bankField.value.currentVal)
  const isTouchedBankSelectField = computed(() => bankField.value.isTouchedField)
  const isShownSomeSelectScreen = computed(() => isShownSelectLanguageScreen.value)

  const setShownLanguages = (status: boolean = true) => {
    isShownLanguages.value = status
  }

  const setShownSelectLanguageScreen = (status: boolean = true) => {
    isShownSelectLanguageScreen.value = status
  }

  const setSelectedPoint = (point: PointType | null) => {
    selectedPoint.value = point
  }

  const setSelectedTransaction = (transaction: TransactionType | null) => {
    selectedTransaction.value = transaction
  }

  const setSelectedSettlement = (settlement: SettlementType | null) => {
    selectedSettlement.value = settlement
  }

  const setSelectedUser = (user: UserType | null) => {
    selectedUser.value = user
  }

  const setShownSelectPaymentMethodScreen = (status: boolean = true) => {
    paymentMethodField.value.isShown = status
  }

  const setTouchedPaymentMethodSelectField = (status: boolean = true) => {
    paymentMethodField.value.isTouchedField = status
  }

  const setSelectedPaymentMethod = (paymentMethod: PointPaymentMethodType | null) => {
    paymentMethodField.value.currentVal = paymentMethod
  }

  const setSelectedPayoutMethod = (payoutMethod: PayoutMethodType | null) => {
    selectedPayoutMethod.value = payoutMethod
  }

  const setSelectedBlockchain = (blockchain: CryptoWalletBlockchainType) => {
    selectedBlockchain.value = blockchain
  }

  const setShownSelectCountryScreen = (status: boolean = true) => {
    countryField.value.isShown = status
  }

  const setTouchedCountrySelectField = (status: boolean = true) => {
    countryField.value.isTouchedField = status
  }

  const setSelectedCountry = (country: CountryType | null) => {
    countryField.value.currentVal = country
  }

  const setShownSelectBankScreen = (status: boolean = true) => {
    bankField.value.isShown = status
  }

  const setTouchedBankSelectField = (status: boolean = true) => {
    bankField.value.isTouchedField = status
  }

  const setSelectedBank = (bank: string) => {
    bankField.value.currentVal = bank
  }

  const resetPaymentMethodSelections = () => {
    setShownSelectPaymentMethodScreen(false)
    setSelectedPaymentMethod(null)
    setTouchedPaymentMethodSelectField(false)
  }

  const resetCountrySelections = () => {
    setShownSelectCountryScreen(false)
    setSelectedCountry(null)
    setTouchedCountrySelectField(false)
  }

  const resetBankSelections = () => {
    setShownSelectBankScreen(false)
    setSelectedBank('')
    setTouchedBankSelectField(false)
  }

  const reset = () => {
    resetPaymentMethodSelections()
    resetCountrySelections()
    resetBankSelections()

    isShownLanguages.value = false
    isShownSelectLanguageScreen.value = false
    selectedPoint.value = null
    selectedTransaction.value = null
    selectedSettlement.value = null
    selectedUser.value = null
  }

  return {
    isShownLanguages,
    isShownSelectLanguageScreen,
    selectedPoint,
    selectedTransaction,
    selectedSettlement,
    selectedUser,
    selectedPayoutMethod,
    selectedBlockchain,
    isShownSelectPaymentMethodScreen,
    selectedPaymentMethod,
    isTouchedPaymentMethodSelectField,
    isShownSelectCountryScreen,
    selectedCountry,
    isTouchedCountrySelectField,
    isShownSelectBankScreen,
    selectedBank,
    isTouchedBankSelectField,
    isShownSomeSelectScreen,
    setShownLanguages,
    setShownSelectLanguageScreen,
    setSelectedPoint,
    setSelectedTransaction,
    setSelectedSettlement,
    setSelectedUser,
    setShownSelectPaymentMethodScreen,
    setSelectedPaymentMethod,
    setSelectedPayoutMethod,
    setSelectedBlockchain,
    setTouchedPaymentMethodSelectField,
    setShownSelectCountryScreen,
    setSelectedCountry,
    setTouchedCountrySelectField,
    setShownSelectBankScreen,
    setTouchedBankSelectField,
    setSelectedBank,
    reset
  }
})
